import { useState, useCallback, useEffect } from 'react';
import {
  Datagrid,
  DateField,
  FunctionField,
  ListContextProvider,
  Pagination,
  TextField,
  useList,
} from 'react-admin';
import { AdminPromocodesResponseDto } from 'shared/api';
import { API_BASE_URL_ADMIN } from 'shared/config';
import { LABELS } from 'shared/constants/labels';
import { httpClient } from 'shared/services/http-client';
import EntityLink from 'shared/components/EntityLink';

interface PromocodeListProps {
  url: string;
  page: number;
  perPage: number;
  setPage: (page: number) => void;
  setPerPage: (perPage: number) => void;
}

export const PromocodeList = ({ url, page, perPage, setPage, setPerPage }: PromocodeListProps) => {
  const [data, setData] = useState<AdminPromocodesResponseDto>({ data: [], total: 0, count: 0, page: 1, totalPage: 1 });
  const [isLoading, setIsLoading] = useState(false);

  const fetchPromocodes = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await httpClient(API_BASE_URL_ADMIN + url, {
        method: 'GET',
      });
      const result = await response.json;
      setData(result);
    } catch (error) {
      console.error('Ошибка при загрузке промокодов:', error);
    } finally {
      setIsLoading(false);
    }
  }, [url]);

  useEffect(() => {
    fetchPromocodes();
  }, [fetchPromocodes]);

  const packagesWithId = data?.data.map((item) => ({
    ...item,
    id: item.id,
  }));
  
  const baseContext = useList({ data: packagesWithId, page, perPage });
  const listContext = {
    ...baseContext,
    total: data?.total ?? 0,
  };

  if (isLoading && !data.data.length) return <div>Загрузка...</div>;

  return (
    <ListContextProvider value={listContext}>
      <Datagrid bulkActionButtons={false}>
        <FunctionField
          label={LABELS.promocodeCode}
          render={(record: AdminPromocodesResponseDto) => (
            <EntityLink 
              record={record} 
              idField="id"
              basePath="/promocodes" 
              tooltipText="Перейти к промокоду"
              labelField="code"
            />
          )}
        />
        <FunctionField
          label={LABELS.promocodeType}
          render={(record: { discountType: string }) => {
            const discountTypeMap: Record<string, string> = {
              'amount': 'номинал',
              'percentage': 'процент'
            };

            return discountTypeMap[record.discountType] || record.discountType;
          }}
        />
        <TextField source="amount" label={LABELS.promocodeAmount} />
        <TextField source="percentage" label={LABELS.promocodePercentage} />
        <FunctionField
            label={LABELS.promocodeTimesUsed}
            render={({ useLimitPerUser, timesUsed}: { useLimitPerUser: number, timesUsed: number }) => {
              return `${timesUsed} из ${useLimitPerUser}`;
            }}
        />
        <TextField source="consumerName" label={LABELS.promocodeConsumerName} />
        <TextField source="consumerEmail" label={LABELS.promocodeConsumerEmail} />
        <TextField source="consumerRole" label={LABELS.companyRole} />
        <TextField source="consumerGroup" label={LABELS.companyGroup} />
        <DateField
          sortable={false}
          source="expireDate"
          label={LABELS.promocodeExpireDate}
        />
        <DateField
          sortable={false}
          source="createdAt"
          label={LABELS.createdAt}
        />
        <TextField source="adminName" label={LABELS.promocodeCreatorName} />
      </Datagrid>
      <Pagination
        page={page}
        perPage={perPage}
        setPage={setPage}
        setPerPage={setPerPage}
        total={listContext.total}
        isLoading={isLoading}
      />
    </ListContextProvider>
  );
};
